import { fetchJSON as fetchAPI } from './client';

export type InventoryGridRow = {
  id: string;
  asin?: string;
  sku?: string;
  marketplace_id?: string;
  channel: string;
  sellable: number;
  available: number;
  inbound: number;
  coverage_days: number;
  stockout_risk: boolean;
  warehouse_id?: string;
  updated_at: string;
};

export type DashboardSummary = {
  tenant_id: string;
  workspace_id?: string;
  total_items: number;
  active_items: number;
  total_sellable: number;
  stockout_count: number;
  overstock_count: number;
  aging_count: number;
  pending_bulk_jobs: number;
  unread_alerts: number;
  indexed_items: number;
  by_channel?: Record<string, number>;
  recent_activity?: ActivityEntry[];
  top_items?: InventoryGridRow[];
  generated_at: string;
};

export type InventoryHealthCenter = {
  tenant_id: string;
  overall_health: number;
  stockout_rate: number;
  overstock_rate: number;
  aging_rate: number;
  avg_coverage_days: number;
  issues_by_type: Record<string, number>;
  top_issues?: { item_id: string; sku?: string; code: string; message: string; severity: string }[];
  generated_at: string;
};

export type InventoryItem = {
  id: string;
  workspace_id: string;
  identifiers: { asin?: string; sku?: string; fnsku?: string };
  marketplace_id?: string;
  channel: string;
  quantities: { available: number; sellable: number; reserved?: number; inbound?: number };
  warehouse?: { fulfillment_center_id?: string; region_code?: string };
  replenishment?: {
    safety_stock?: number;
    reorder_threshold?: number;
    lead_time_days?: number;
    days_of_supply?: number;
    consumption_rate?: number;
  };
  health?: { stockout_risk?: boolean; overstock_risk?: boolean; coverage_days?: number };
  status: string;
  updated_at: string;
};

export type SearchResult = {
  items: InventoryItem[];
  total: number;
  limit: number;
  offset: number;
  facets?: {
    channel?: Record<string, number>;
    marketplace?: Record<string, number>;
    status?: Record<string, number>;
  };
  took_ms?: number;
};

export type ActivityEntry = {
  id: string;
  item_id?: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type InventoryAlert = {
  id: string;
  item_id?: string;
  title: string;
  body?: string;
  severity: string;
  read: boolean;
  created_at: string;
};

export type AIRecommendation = {
  id: string;
  item_id: string;
  type: string;
  title: string;
  description: string;
  impact: number;
  confidence: number;
  field?: string;
};

export type DemandForecast = {
  item_id: string;
  period: string;
  forecast_units: number;
  confidence: number;
  trend?: string;
  seasonality_index: number;
  generated_at: string;
};

export type ReplenishmentPlan = {
  item_id: string;
  safety_stock: number;
  reorder_point: number;
  recommended_order_qty: number;
  lead_time_days: number;
  days_of_supply: number;
  generated_at: string;
};

export type BulkOperation = {
  id: string;
  action: string;
  state: string;
  item_ids: string[];
  progress: number;
  processed: number;
  failed: number;
  total: number;
  created_at: string;
};

export type WarehouseView = {
  fulfillment_center_id: string;
  region_code?: string;
  item_count: number;
  total_sellable: number;
  total_inbound: number;
  stockout_count: number;
};

export type MultiLocationInventory = {
  item_id: string;
  sku?: string;
  total_sellable: number;
  locations: {
    fulfillment_center_id: string;
    region_code?: string;
    channel: string;
    sellable: number;
    available: number;
    inbound: number;
  }[];
};

const API_BASE = import.meta.env.VITE_AMAZON_INVENTORY_API ?? '/api/v1/amazon/inventory';

async function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const amazonInventoryApi = {
  dashboard: (workspaceId?: string) =>
    fetchJSON<DashboardSummary>(`/dashboard${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  healthCenter: (workspaceId?: string) =>
    fetchJSON<InventoryHealthCenter>(`/health-center${workspaceId ? `?workspace_id=${workspaceId}` : ''}`),
  inventoryGrid: (workspaceId?: string, limit = 50, offset = 0) =>
    fetchJSON<{ rows: InventoryGridRow[]; total: number }>(
      `/grid?workspace_id=${workspaceId ?? ''}&limit=${limit}&offset=${offset}`,
    ),
  globalSearch: (text: string, workspaceId?: string) =>
    fetchJSON<SearchResult>('/search/global', {
      method: 'POST',
      body: JSON.stringify({ text, workspace_id: workspaceId ?? '', limit: 50 }),
    }),
  getItem: (id: string) => fetchJSON<InventoryItem>(`/items/${id}`),
  generateForecast: (id: string) =>
    fetchJSON<DemandForecast>(`/items/${id}/forecast`, { method: 'POST' }),
  seasonality: (id: string) => fetchJSON<{ seasonality_factor: number }>(`/items/${id}/seasonality`),
  replenishmentPlan: (id: string) =>
    fetchJSON<ReplenishmentPlan>(`/items/${id}/replenishment`, { method: 'POST' }),
  recommendations: (id: string) => fetchJSON<AIRecommendation[]>(`/items/${id}/recommendations`),
  multiLocation: (sku: string) => fetchJSON<MultiLocationInventory>(`/multi-location?sku=${encodeURIComponent(sku)}`),
  warehouseViews: (workspaceId?: string) =>
    fetchJSON<WarehouseView[]>(`/warehouses?workspace_id=${workspaceId ?? ''}`),
  triggerBulk: (body: { action: string; item_ids: string[]; workspace_id: string; parameters?: Record<string, unknown> }) =>
    fetchJSON<BulkOperation>('/bulk', { method: 'POST', body: JSON.stringify(body) }),
  listBulk: (workspaceId?: string) =>
    fetchJSON<BulkOperation[]>(`/bulk?workspace_id=${workspaceId ?? ''}`),
  listActivity: (itemId?: string, limit = 50) =>
    fetchJSON<ActivityEntry[]>(`/activity${itemId ? `?item_id=${itemId}&limit=${limit}` : `?limit=${limit}`}`),
  listAlerts: (unread = false) => fetchJSON<InventoryAlert[]>(`/alerts?unread=${unread}`),
  markAlertRead: (id: string) =>
    fetchJSON<{ message: string }>(`/alerts/${id}/read`, { method: 'POST' }),
  adminHealth: () => fetchJSON<Record<string, unknown>>('/admin/health'),
  diagnostics: () => fetchJSON<Record<string, unknown>>('/diagnostics'),
  rebuildIndex: () => fetchJSON<{ indexed: number }>('/index/rebuild', { method: 'POST' }),
};

export const BULK_ACTIONS = ['recalculate_health', 'forecast', 'archive'] as const;
